import React from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { Button } from './Button';

export const CartSidebar: React.FC = () => {
  const { items, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, cartTotal } = useCart();
  const navigate = useNavigate();

  if (!isCartOpen) return null;

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-stone-950/40 backdrop-blur-sm transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Sidebar Panel */}
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-300 animate-[slideIn_0.3s_ease-out] border-l border-orange-100">
        <div className="p-6 border-b border-orange-100 flex items-center justify-between bg-white">
          <h2 className="text-lg font-serif font-bold text-stone-900 uppercase tracking-widest">Shopping Cart</h2>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="text-stone-500 hover:text-stone-900"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center text-stone-400 border border-orange-100">
                <X size={32} />
              </div>
              <p className="text-sm text-stone-600 font-black uppercase tracking-widest">Your cart is empty.</p>
              <Button onClick={() => setIsCartOpen(false)} variant="outline">
                Start Shopping
              </Button>
            </div>
          ) : (
            items.map((item) => {
              const signature = JSON.stringify(item.selectedVariants || []);
              return (
              <div key={`${item.id}-${signature}`} className="flex gap-4 p-4 bg-white rounded-2xl border border-orange-100">
                <div className="w-16 h-16 rounded-2xl overflow-hidden border border-orange-100 flex-shrink-0">
                  {item.images && item.images.length > 0 ? (
                    <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-orange-50 flex items-center justify-center text-orange-200">
                      <ShoppingBag size={16} />
                    </div>
                  )}
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start">
                      <h3 className="font-black text-sm text-stone-900 line-clamp-2 uppercase tracking-wide">{item.name}</h3>
                      <button 
                        onClick={() => removeFromCart(item.id, signature)}
                        className="text-stone-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="text-[9px] text-stone-500 font-black uppercase tracking-widest mt-1">{item.category}</p>
                    {Array.isArray(item.selectedVariants) && item.selectedVariants.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {item.selectedVariants.map((v, idx) => (
                          <p key={idx} className="text-[8px] text-stone-600 font-black uppercase tracking-widest">
                            {v.groupName}: {v.type === 'numeric' ? `${v.value}${v.unit}` : v.optionName}
                          </p>
                        ))}
                      </div>
                    )}
                    <p className="text-[8px] text-green-600 font-black uppercase tracking-widest mt-1">In Stock</p>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-orange-200 rounded-xl bg-white">
                      <button 
                        onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1), signature)}
                        className="p-1 px-2 hover:bg-orange-50 text-stone-700 rounded-l-xl"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-8 text-center text-[9px] font-black text-stone-900">{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1, signature)}
                        className="p-1 px-2 hover:bg-orange-50 text-stone-700 rounded-r-xl"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <p className="font-black text-stone-900 text-sm uppercase tracking-wide">₹{(((typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice)) ? item.unitPrice : item.currentPrice) * item.quantity).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 bg-white border-t border-orange-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-stone-600 text-[9px] font-black uppercase tracking-widest">Subtotal ({items.reduce((a, b) => a + b.quantity, 0)} items):</span>
              <span className="text-lg font-serif font-bold text-stone-900 uppercase tracking-tighter">₹{cartTotal.toLocaleString('en-IN')}</span>
            </div>
            <Button className="w-full mt-4" onClick={handleCheckout}>
              Proceed to Checkout
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};